(() => {
  window.ElevarDataLayer = window.ElevarDataLayer ?? [];

  const currencyCode = () => (
    window.FAFThemeConfig?.currencyCode ||
    window.Shopify?.currency?.active ||
    'GBP'
  );

  const stringValue = (value, fallback = '') => (
    value === null || value === undefined ? fallback : String(value)
  );

  const moneyFromMinor = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? (number / 100).toFixed(2) : '0.00';
  };

  const absoluteUrl = (value) => {
    if (!value) return '';

    try {
      return new URL(String(value), window.location.origin).href;
    } catch (error) {
      return '';
    }
  };

  const imageUrl = (value) => {
    const source = value?.url || value?.src || value || '';
    if (!source) return '';
    if (String(source).startsWith('//')) return `https:${source}`;
    return absoluteUrl(source);
  };

  const productFromAjax = (item = {}, options = {}) => {
    const variantId = Number(
      options.variantId ?? item.variant_id ?? item.variantId ?? item.id ?? 0
    );
    const productId = Number(
      options.productId ?? item.product_id ?? item.productId ?? 0
    );
    const quantity = Number(options.quantity ?? item.quantity ?? 1);
    const priceMinor = Number(
      options.priceMinor ?? item.final_price ?? item.price ?? 0
    );
    const compareAtMinor = Number(
      options.compareAtMinor ?? item.compare_at_price ?? 0
    );

    return {
      id: stringValue(options.sku ?? item.sku ?? variantId),
      name: stringValue(
        options.name ?? item.product_title ?? item.title ?? ''
      ),
      brand: stringValue(options.brand ?? item.vendor ?? ''),
      category: stringValue(
        options.category ?? item.product_type ?? ''
      ),
      variant: stringValue(
        options.variant ?? item.variant_title ?? ''
      ),
      price: moneyFromMinor(priceMinor),
      quantity: stringValue(Math.max(1, quantity)),
      position: options.position ? Number(options.position) : undefined,
      list: stringValue(options.list ?? window.location.pathname),
      product_id: stringValue(productId),
      variant_id: stringValue(variantId),
      compare_at_price: moneyFromMinor(compareAtMinor),
      image: imageUrl(
        options.image ?? item.featured_image ?? item.image ?? ''
      ),
      url: absoluteUrl(options.url ?? item.url ?? '')
    };
  };

  const productFromConfig = (config = {}, options = {}) => ({
    id: stringValue(options.sku ?? config.sku ?? config.variantId ?? config.productId),
    name: stringValue(options.name ?? config.name ?? config.title ?? ''),
    brand: stringValue(options.brand ?? config.brand ?? config.vendor ?? ''),
    category: stringValue(options.category ?? config.category ?? ''),
    variant: stringValue(options.variant ?? config.variant ?? config.variantTitle ?? ''),
    price: moneyFromMinor(options.priceMinor ?? config.priceMinor ?? 0),
    quantity: stringValue(Math.max(1, Number(options.quantity ?? 1))),
    position: options.position ? Number(options.position) : undefined,
    list: stringValue(options.list ?? config.list ?? window.location.pathname),
    product_id: stringValue(options.productId ?? config.productId ?? 0),
    variant_id: stringValue(options.variantId ?? config.variantId ?? 0),
    compare_at_price: moneyFromMinor(
      options.compareAtMinor ?? config.compareAtMinor ?? 0
    ),
    image: imageUrl(options.image ?? config.image ?? ''),
    url: absoluteUrl(options.url ?? config.url ?? '')
  });

  const cleanProduct = (product = {}) => Object.fromEntries(
    Object.entries(product).filter(([, value]) => value !== undefined)
  );

  const productConfigFromCard = (card) => {
    const script = card?.querySelector?.('script[data-elevar-product]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      return null;
    }
  };

  const cardsInRoot = (root) => Array.from(
    root?.querySelectorAll?.('[data-elevar-product-card]') || []
  );

  const productsFromRoot = (root, options = {}) => cardsInRoot(root)
    .map((card, index) => {
      const config = productConfigFromCard(card);
      if (!config) return null;

      return cleanProduct(productFromConfig(config, {
        list: options.list || root?.dataset?.elevarList || window.location.pathname,
        position: index + 1,
        quantity: options.quantity || 1
      }));
    })
    .filter(Boolean);

  const push = (payload) => {
    if (!payload?.event) return false;
    window.ElevarDataLayer.push(payload);
    return true;
  };

  const ecommerceEvent = ({ event, action, product, list }) => push({
    event,
    ecommerce: {
      currencyCode: currencyCode(),
      [action]: {
        actionField: {
          list: list || product.list || window.location.pathname
        },
        products: [cleanProduct(product)]
      }
    }
  });

  const api = {
    currencyCode,
    productFromAjax,
    productFromConfig,

    readProduct(element) {
      const root = element?.closest?.(
        '[data-elevar-product-card], [data-elevar-product-root]'
      ) || element;

      return productConfigFromCard(root);
    },

    addToCart(item, options = {}) {
      const product = options.fromConfig
        ? productFromConfig(item, options)
        : productFromAjax(item, options);
      return ecommerceEvent({
        event: 'dl_add_to_cart',
        action: 'add',
        product,
        list: options.source || options.list
      });
    },

    removeFromCart(item, options = {}) {
      const product = options.fromConfig
        ? productFromConfig(item, options)
        : productFromAjax(item, options);
      return ecommerceEvent({
        event: 'dl_remove_from_cart',
        action: 'remove',
        product,
        list: options.source || options.list
      });
    },

    viewCart(cart = {}) {
      const products = (cart.items || []).map((item, index) => cleanProduct(
        productFromAjax(item, {
          position: index + 1,
          list: 'cart_drawer'
        })
      ));

      return push({
        event: 'dl_view_cart',
        cart_total: moneyFromMinor(cart.total_price),
        ecommerce: {
          currencyCode: cart.currency || currencyCode(),
          actionField: {},
          impressions: products
        }
      });
    },

    addToWishlist(product, options = {}) {
      const normalised = options.fromAjax
        ? productFromAjax(product, options)
        : productFromConfig(product, options);

      return push({
        event: 'dl_add_to_wishlist',
        event_properties: {
          currency_code: currencyCode(),
          source: options.source || normalised.list,
          products: [cleanProduct(normalised)]
        }
      });
    },

    selectItem(product, options = {}) {
      const normalised = options.fromAjax
        ? productFromAjax(product, options)
        : productFromConfig(product, options);

      return push({
        event: 'dl_select_item',
        ecommerce: {
          currencyCode: currencyCode(),
          click: {
            actionField: {
              list: options.list || normalised.list || window.location.pathname,
              action: 'click'
            },
            products: [cleanProduct(normalised)]
          }
        }
      });
    },

    viewItemList(root, options = {}) {
      const impressions = productsFromRoot(root, options);
      if (!impressions.length) return false;

      return push({
        event: 'dl_view_item_list',
        ecommerce: {
          currencyCode: currencyCode(),
          impressions
        }
      });
    },

    viewItemListCards(cards = [], options = {}) {
      const list = options.list || window.location.pathname;
      const impressions = Array.from(cards)
        .map((card, index) => {
          const product = productConfigFromCard(card);
          if (!product) return null;

          return cleanProduct(productFromConfig(product, {
            list,
            position: Number(options.startPosition || 0) + index + 1
          }));
        })
        .filter(Boolean);

      if (!impressions.length) return false;

      return push({
        event: 'dl_view_item_list',
        ecommerce: {
          currencyCode: currencyCode(),
          impressions
        }
      });
    },

    viewSearchResults(root, options = {}) {
      const list = options.list || 'search_results';
      const impressions = productsFromRoot(root, { ...options, list });

      return push({
        event: 'dl_view_search_results',
        search_term: options.searchTerm || '',
        ecommerce: {
          currencyCode: currencyCode(),
          actionField: { list },
          impressions
        }
      });
    },

    customizeItem(product, options = {}) {
      const normalised = options.fromAjax
        ? productFromAjax(product, options)
        : productFromConfig(product, options);

      return push({
        event: 'dl_customize_item',
        event_properties: {
          currency_code: currencyCode(),
          source: options.source || normalised.list,
          products: [cleanProduct(normalised)]
        }
      });
    },

    subscribe({ leadType, email = '', phone = '' } = {}) {
      const type = leadType === 'phone' ? 'phone' : 'email';
      const userProperties = {};

      if (email) userProperties.customer_email = email;
      if (phone) userProperties.customer_phone = phone;

      return push({
        event: 'dl_subscribe',
        lead_type: type,
        user_properties: userProperties
      });
    },

    custom(event, eventProperties = {}) {
      const elevarTracked = push({
        event,
        event_properties: eventProperties
      });

      window.dataLayer = window.dataLayer || [];

      const gtmProperties = Object.fromEntries(
        Object.entries(eventProperties).map(([key, value]) => {
          const parameterName = key.startsWith('faf_')
            ? key
            : `faf_${key}`;

          return [
            parameterName,
            Array.isArray(value) ? value.join(',') : value
          ];
        })
      );

      window.dataLayer.push({
        event,
        ...gtmProperties,
        event_properties: eventProperties
      });

      return elevarTracked;
    }
  };

  window.FAFAnalytics = api;

  const trackInitialLists = (root = document) => {
    root
      .querySelectorAll('[data-elevar-list]')
      .forEach((list) => {
        if (list.dataset.elevarTracked === 'true') return;
        if (!list.querySelector('[data-elevar-product-card]')) return;

        const tracked = api.viewItemList(list, {
          list: list.dataset.elevarList || window.location.pathname
        });

        if (tracked) {
          list.dataset.elevarTracked = 'true';
        }
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => trackInitialLists(),
      { once: true }
    );
  } else {
    trackInitialLists();
  }

  window.addEventListener('faf:track-product-lists', (event) => {
    trackInitialLists(event.detail?.root || document);
  });

  let cartViewTracked = false;

  window.addEventListener('lazy-panel:ready', async (event) => {
    if (!window.FAFThemeConfig?.elevarManualViewCart) return;
    if (event.detail?.panel !== 'push-cart') return;
    if (window.Alpine?.store?.('cart')?.mode !== 'cart') return;
    if (cartViewTracked) return;

    cartViewTracked = true;

    try {
      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}cart.js`,
        { headers: { Accept: 'application/json' } }
      );

      if (!response.ok) throw new Error('Cart view could not be read.');
      api.viewCart(await response.json());
    } catch (error) {
      cartViewTracked = false;
    }
  });

  window.addEventListener('lazy-panel:closed', (event) => {
    if (event.detail?.panel === 'push-cart') {
      cartViewTracked = false;
    }
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest?.('[data-elevar-product-card] a[href*="/products/"]');
    const card = link?.closest?.('[data-elevar-product-card]');

    if (!card) return;

    const product = productConfigFromCard(card);
    if (!product) return;

    const root = card.closest('[data-elevar-list]') || card.parentElement;
    const cards = cardsInRoot(root);
    const position = Math.max(1, cards.indexOf(card) + 1);

    api.selectItem(product, {
      list: root?.dataset?.elevarList || window.location.pathname,
      position
    });
  }, true);

  window.addEventListener('faf:size-preference-changed', (event) => {
    api.custom('faf_size_system_changed', {
      size_system: event.detail?.system || '',
      country: window.FAFThemeConfig?.countryCode || '',
      surface: window.FAFThemeConfig?.pageType || 'storefront'
    });
  });
})();
